"use client";

import { usePathname } from "next/navigation";
import ArticleFooter from "@/app/components/ArticleFooter";
import SiteFooter from "@/app/components/SiteFooter";
import StudyResourceFooter from "@/app/components/study/StudyResourceFooter";
import { HUB_SUBJECTS } from "@/lib/subject-guard";
import { isPublishedMandarinPath } from "@/lib/i18n/publication";
import type { H2StudySubject } from "@/lib/study/h2-study-topics";

const HUB_SUBJECT_SET = new Set<string>(HUB_SUBJECTS);

const H2_QUIZ_TO_NOTES: Record<string, H2StudySubject> = {
  "h2-maths": "h2-maths-notes",
  "h2-physics": "h2-physics-notes",
  "h2-chemistry": "h2-chemistry-notes",
  "h2-biology": "h2-biology-notes",
};

function resolveStudyFooterSubject(pathname: string): H2StudySubject | null {
  const notesMatch = pathname.match(
    /^\/blog\/(h2-(?:maths|physics|chemistry|biology)-notes)(?:\/|$)/
  );
  if (notesMatch) return notesMatch[1] as H2StudySubject;

  const quizMatch = pathname.match(/^\/quizzes\/(h2-[^/]+)$/);
  if (quizMatch) return H2_QUIZ_TO_NOTES[quizMatch[1]] ?? null;

  const videoMatch = pathname.match(
    /^\/videos\/(h2-(?:maths|physics|chemistry|biology)-notes)(?:\/|$)/
  );
  return videoMatch ? (videoMatch[1] as H2StudySubject) : null;
}

function isBlogArticlePath(pathname: string): boolean {
  const segments = pathname.split("/").filter(Boolean);
  if (segments[0] !== "blog" || segments.length < 2) return false;
  if (segments.length >= 3) return true;
  return !HUB_SUBJECT_SET.has(segments[1]);
}

export default function ConditionalFooter() {
  const rawPathname = usePathname();
  const publicPathname = rawPathname?.replace(/^\/zh-Hans(?=\/|$)/, "/zh");
  // Mandarin blog layouts own their localized footer, including draft previews.
  if (
    publicPathname?.startsWith("/zh/blog/") ||
    (publicPathname && isPublishedMandarinPath(publicPathname))
  )
    return null;

  const pathname =
    rawPathname?.replace(/^\/(?:en|zh-Hans|zh)(?=\/|$)/, "") || "/";
  const hideFooter =
    pathname?.startsWith("/landing") ||
    pathname?.startsWith("/practice/mcq") ||
    pathname === "/locations/henderson/virtual-tour" ||
    /^\/quizzes\/[^/]+\/[^/]+/.test(pathname ?? "");
  if (hideFooter) return null;
  const studySubject = resolveStudyFooterSubject(pathname ?? "");
  if (studySubject || pathname === "/videos") {
    return <StudyResourceFooter subject={studySubject} />;
  }
  if (isBlogArticlePath(pathname ?? "")) {
    return <ArticleFooter />;
  }
  return <SiteFooter />;
}
